// Store minimal (remplace zustand côté Faast) : un état, des abonnés, un
// sélecteur par composant. Une instance par widget monté, jamais globale :
// deux widgets sur la même page ne se mélangent pas.
import { useEffect, useRef, useState } from 'preact/hooks'

export interface Store<T> {
  get: () => T
  set: (patch: Partial<T> | ((s: T) => Partial<T>)) => void
  subscribe: (fn: (s: T) => void) => () => void
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial
  const subs = new Set<(s: T) => void>()
  return {
    get: () => state,
    set: (patch) => {
      const next = typeof patch === 'function' ? patch(state) : patch
      state = { ...state, ...next }
      subs.forEach((fn) => fn(state))
    },
    subscribe: (fn) => {
      subs.add(fn)
      return () => subs.delete(fn)
    },
  }
}

/** Lit une tranche du store ; ne re-rend que si elle change (Object.is). */
export function useSelector<T, R>(store: Store<T>, select: (s: T) => R): R {
  const [value, setValue] = useState(() => select(store.get()))
  const selectRef = useRef(select)
  selectRef.current = select
  useEffect(() => {
    const check = (s: T) => {
      const next = selectRef.current(s)
      setValue((prev) => (Object.is(prev, next) ? prev : next))
    }
    check(store.get())
    return store.subscribe(check)
  }, [store])
  return value
}

/** Émetteur d'événements minimal (temps réel, mutations locales). */
export function createEmitter<E>() {
  const subs = new Set<(e: E) => void>()
  return {
    emit: (e: E) => subs.forEach((fn) => fn(e)),
    on: (fn: (e: E) => void) => {
      subs.add(fn)
      return () => {
        subs.delete(fn)
      }
    },
  }
}
export type Emitter<E> = ReturnType<typeof createEmitter<E>>
